import { useEffect, useRef, useState } from "react";
import { api, errorMessage } from "../../api/client";
import type { FilterNode } from "../../api/types";
import { Icon, useToast } from "../../components/ui";
import { fmt } from "../../lib/format";

/** Export asynchrone : création du job, suivi, puis téléchargement (le fichier ne transite jamais par le JS). */
export function ExportButton({ q, filters, disabled }: { q: string; filters: FilterNode | null; disabled?: boolean }) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState<string | null>(null);
  const toast = useToast();
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const onDown = (e: MouseEvent) => ref.current && !ref.current.contains(e.target as Node) && setOpen(false);
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, []);

  async function run(format: "csv" | "xlsx") {
    setOpen(false);
    setBusy(format);
    try {
      const { id } = await api<{ id: number }>("/exports", { json: { q: q || null, filters, format } });
      for (let i = 0; i < 600; i++) {
        const st = await api<{ status: string; rows: number | null; error: string | null }>(`/exports/${id}`);
        if (st.status === "succeeded") {
          const a = document.createElement("a");
          a.href = `/api/exports/${id}/file`;
          a.rel = "noopener";
          document.body.appendChild(a);
          a.click();
          a.remove();
          toast(`Export prêt : ${fmt(st.rows)} lignes`);
          return;
        }
        if (st.status === "failed") throw new Error(st.error ?? "Export en échec");
        await new Promise((r) => setTimeout(r, i < 10 ? 700 : 2000));
      }
      throw new Error("Export trop long : il reste disponible, réessayez plus tard.");
    } catch (e) {
      toast(errorMessage(e), true);
    } finally {
      setBusy(null);
    }
  }

  return (
    <div style={{ position: "relative" }} ref={ref}>
      <button className="btn" type="button" onClick={() => setOpen((x) => !x)} disabled={disabled || !!busy} aria-haspopup="menu" aria-expanded={open}>
        <Icon name="download" />{busy ? `Export ${busy.toUpperCase()}…` : "Exporter"}
      </button>
      {open && (
        <div className="popover" role="menu" style={{ right: 0, width: 300 }}>
          <button className="btn ghost" role="menuitem" type="button" onClick={() => run("xlsx")} style={{ justifyContent: "flex-start" }}>Excel (.xlsx)</button>
          <button className="btn ghost" role="menuitem" type="button" onClick={() => run("csv")} style={{ justifyContent: "flex-start" }}>CSV (séparateur « ; »)</button>
          <span className="muted" style={{ fontSize: 13 }}>Une ligne par contact, tous les champs exportables, y compris les données ajoutées par les imports.</span>
        </div>
      )}
    </div>
  );
}
